/**
 * RegisterReconciliationModal — Modal para marcar un periodo como conciliado
 */

import { useState } from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface SummaryItem {
  key: string;
  label: string;
  color: string; // tailwind text color class, e.g. 'text-emerald-600'
  isError?: boolean; // if true, counts toward discrepancy check
}

const COLLECTOR_ITEMS: SummaryItem[] = [
  { key: 'ok', label: 'OK', color: 'text-emerald-600' },
  { key: 'descuadre', label: 'Descuadre', color: 'text-amber-600', isError: true },
  { key: 'soloCC', label: 'Solo CC', color: 'text-red-600', isError: true },
  { key: 'soloCR', label: 'Solo CR', color: 'text-red-600', isError: true },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  resumen: { totals: Record<string, number> };
  isRegistering: boolean;
  /** Label above summary, e.g. "CC ↔ CR" */
  summaryLabel?: string;
  /** Override summary display items */
  summaryItems?: SummaryItem[];
}

export function RegisterReconciliationModal({
  isOpen, onClose, onConfirm, resumen, isRegistering,
  summaryLabel = 'CC ↔ CR (CreditContract vs CollectionRecord)',
  summaryItems = COLLECTOR_ITEMS,
}: Props) {
  const [notes, setNotes] = useState('');

  const hasErrors = summaryItems
    .filter((s) => s.isError)
    .some((s) => (resumen.totals[s.key] ?? 0) > 0);

  const status = hasErrors ? 'CON_DISCREPANCIAS' : 'LIMPIO';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar conciliacion" size="md">
      <div className="space-y-4">
        {/* Status */}
        <div className="flex items-center gap-2">
          {hasErrors ? (
            <AlertTriangle size={20} className="text-amber-500" />
          ) : (
            <CheckCircle size={20} className="text-emerald-500" />
          )}
          <Badge variant={hasErrors ? 'warning' : 'success'}>{status}</Badge>
          <span className="text-sm text-muted-foreground">
            {hasErrors ? 'Se encontraron discrepancias' : 'Sin errores criticos'}
          </span>
        </div>

        {/* Summary */}
        <div className="rounded-md border border-border p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">{summaryLabel}</p>
          <div className="flex items-center gap-4 text-xs tabular-nums">
            {summaryItems.map((item) => {
              const count = resumen.totals[item.key] ?? 0;
              if (count === 0 && item.key !== 'ok') return null;
              return (
                <span key={item.key} className={item.color}>
                  {count} {item.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="recon-notes" className="mb-1 block text-sm font-medium">
            Notas (opcional)
          </label>
          <textarea
            id="recon-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Observaciones sobre esta corrida..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="outline" onClick={onClose} disabled={isRegistering}>
            Cancelar
          </Button>
          <Button onClick={() => onConfirm(notes)} disabled={isRegistering}>
            {isRegistering ? 'Registrando...' : 'Confirmar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
